import type { EnrichmentPostStepFlags } from '~/types/enrichment-flags';
import type { LiveStage } from '~/types/live-stage';
import type { SourceShippingAttributes } from '~/types/source-shipping';

import type { MetaDetails } from './scheduled-enrichment.types';

/**
 * Types for the experiment-scoped content-scoring workflow.
 *
 * - `POST /workflow/v1/experiments/{id}/content-scoring/trigger` — kicks off
 *   the scoring run; returns the workflow + run identifiers and an initial
 *   status (typically `IN_PROGRESS`).
 * - `GET /workflow/v1/experiments/{id}/content-scoring/status` — polled
 *   while the run is in flight; returns progress counters used to render
 *   the step-2 (Generate Sample Content) page.
 * - `GET /workflow/v1/experiments/{id}/content-scoring/results` — paginated
 *   per-product scoring results. Drives the "Preview Products" tab on the
 *   Enrichment Overview page.
 */

export type ContentScoringStatus = 'IN_PROGRESS' | 'COMPLETED' | 'FAILED' | (string & {});

export interface ContentScoringTriggerResponse {
  workflowId: string;
  runId: string;
  status: ContentScoringStatus;
}

export interface ContentScoringStatusResponse {
  experimentId: string;
  workflowId: string;
  scoringStatus: ContentScoringStatus;
  /** ISO timestamp — when the workflow first kicked off. */
  startedAt: string;
  /** ISO timestamp — populated once the workflow is in a terminal state. */
  completedAt: string | null;
  /** Total products in the scoring scope. `null` until the workflow figures it out. */
  totalProducts: number | null;
  scoredProducts: number;
  failedProducts: number;
  /** Set when `scoringStatus = FAILED` — actionable explanation of why scoring produced no
   *  results (e.g. the catalog snapshot was superseded and no products matched). `null` otherwise. */
  failureReason?: string | null;
  /** Real-time per-stage breakdown (FETCH → SPLIT → SAMPLE_SELECT → SCORING);
   *  absent on older responses. Drives the live sub-step list in the loader. */
  liveStage?: LiveStage | null;
}

/**
 * Per-attribute content quality scores, each in the 0–10 range. Fields are dynamic —
 * the backend returns whatever attributes were scored (e.g. title, description, productType).
 * Every value is `null` (and `productCount = 0`) when scoring has never run.
 */
export type ProductContentQualityScore = Record<string, number | null>;

/**
 * Aggregate quality scores from the latest content-scoring run. Drives the
 * `QualityScoreCard` gauge (0–100) and the `ScoreBarChartCard` bar
 * chart (0–10 per attribute) on the Current Content Score page.
 */
export interface ContentScoringQualityScoreResponse {
  experimentId: string;
  workflowId: string;
  /** Terminal scoring state for the run. Lets the empty-state distinguish "no data yet"
   *  (null / IN_PROGRESS) from "the run finished but produced nothing" (FAILED). */
  scoringStatus?: ContentScoringStatus | null;
  /** Set when `scoringStatus = FAILED` — why the run produced no scores (e.g. the catalog
   *  snapshot was superseded and no products matched the scope filters). `null` on success. */
  failureReason?: string | null;
  /** Total scored rows. `0` for empty runs, `null` observed in the wild when
   *  the workflow completes without dispatching any products — treat both as
   *  "scoring effectively empty" per `docs/content-scoring-empty-state.md`. */
  productCount: number | null;
  /** Wire value is 0–10; `useContentScoringQualityScore`'s `select` scales it ×10 to
   *  0–100 for the donut/gauge. `null` when scoring has never run. */
  catalogContentQualityScore: number | null;
  /** Per-attribute breakdown. `null` when scoring has never run; nested fields are `null` for the same reason. */
  productContentQualityScore: ProductContentQualityScore | null;
  /**
   * Per-category 0–10 breakdown for the "Category-Level Quality Score" section.
   * Optional + NOT yet returned by `/content-scoring/quality-score` — the
   * PRE-scoring entity (`ExperimentContentScoreEntity`) has no category column,
   * so the UI renders the section's empty state until the backend adds it.
   * Mirrors the sample/pipeline `categoryScores` shape (`SampleCategoryScore`).
   */
  categoryScores?: SampleCategoryScore[];
  /** Meta Catalog Scorer aggregate data — present only when connectorCode = META. */
  metaDetails?: MetaDetails | null;
}

// ---------------------------------------------------------------------------
// Sample Enrichment — same async-trigger / poll-status shape as content
// scoring. Server picks up to `sampleSize` (default 10) candidate products
// from the most recent content-scoring run, falling back to a random sample
// when none are flagged.
// ---------------------------------------------------------------------------

export type SampleEnrichmentStatus = 'IN_PROGRESS' | 'COMPLETED' | 'FAILED' | (string & {});

/**
 * Surface for which the optimized copy is being generated. Server derives
 * this from `experiment.adPlatform` + `experiment.contentAttributes` when
 * the trigger body is omitted; callers can override per-run if needed.
 */
export type UseCaseType = 'GMC_ADS' | 'META_ADS' | 'ITEM_SETUP' | 'ONSITE_SEARCH';

/**
 * Subset of GMC Ads fields the backend can enrich. `useCase.fields` is only
 * honored when `useCase.type === 'GMC_ADS'` (per UX integration spec).
 * Mirrors the enum reference table in `UX-API-integration.pdf`. Kept as a
 * runtime array (not just a type) so a config-service key can be validated
 * against it before being cast — the backend enum has no leniency, so an
 * unrecognised name is an HTTP 400, not a silent drop.
 */
export const GMC_ADS_FIELDS = [
  'TITLE',
  'STRUCTURED_TITLE',
  'DESCRIPTION',
  'STRUCTURED_DESCRIPTION',
  'PRODUCT_TYPE',
  'GOOGLE_PRODUCT_CATEGORY',
  'PRODUCT_HIGHLIGHT',
  'PRODUCT_DETAIL',
  'PRODUCT_QA',
  'FEATURES',
  'SPECS',
  'SHAPES_AND_SILHOUETTES',
  'THEMES_AND_OCCASIONS',
  'STYLE_AND_AESTHETIC',
  'COMPATIBLE_ACCESSORIES',
  'COMPATIBLE_WITH',
  'SUBSTITUTE_PRODUCTS',
  'BUNDLE_SUGGESTIONS',
  'COLOR',
  'MATERIAL',
  'PATTERN',
  'SIZE_TYPE',
  'SUSTAINABILITY_ATTRIBUTES',
  'MATERIAL_ORIGIN',
  'CERTIFICATION',
  'CARE_INSTRUCTION',
  'CONSUMER_NOTICE',
  'CUSTOM_LABEL_0',
  'CUSTOM_LABEL_1',
  'CUSTOM_LABEL_2',
  'CUSTOM_LABEL_3',
  'CUSTOM_LABEL_4',
  'ADDITIONAL_IMAGE_LINK',
  'LIFESTYLE_IMAGE_LINK',
  'NATIVE_COMMERCE',
  'MERCHANT_ITEM_ID',
] as const;

export type GmcAdsField = (typeof GMC_ADS_FIELDS)[number];

/**
 * Optional body for `POST /sample-enrichment/trigger`. When omitted, server
 * defaults `sampleSize=10` and derives `useCase` from the experiment row.
 */
/**
 * Optional body for `POST /experiments/{id}/sample-enrichment/trigger`.
 *
 * Post-step flags sit **top-level**, not nested on `useCase`, deliberately:
 * `useCase` is optional here and the server derives it from the experiment's
 * `adPlatform` + `contentAttributes`. Supplying the object purely to carry flags
 * would mean deriving `type` client-side and overriding that derivation.
 *
 * ```jsonc
 * {
 *   "sampleSize": 10,
 *   "searchDescriptorsEnabled": true,
 *   "phraseDescriptionsEnabled": true
 * }
 * ```
 */
export interface SampleEnrichmentTriggerPayload extends EnrichmentPostStepFlags {
  /** Number of products to dispatch for this sample run. Server caps at its own default. */
  sampleSize?: number;
  useCase?: {
    type: UseCaseType;
    /** GMC_ADS only — restricts which fields the enrichment generates. Ignored for other surfaces. */
    fields?: GmcAdsField[];
  };
}

export interface SampleEnrichmentTriggerResponse {
  workflowId: string;
  runId: string;
  status: SampleEnrichmentStatus;
  /** Products actually dispatched for this run (may be < requested sampleSize). */
  productCount: number;
}

export interface SampleEnrichmentStatusResponse {
  experimentId: string;
  /** `null` when no sample run has been started for the experiment yet. */
  workflowId: string | null;
  /** `null` when no sample run exists yet — the endpoint returns 200 (not 404) in that case. */
  status: SampleEnrichmentStatus | null;
  startedAt: string | null;
  completedAt: string | null;
  totalProducts: number | null;
  succeededProducts: number;
  failedProducts: number;
  /**
   * Human-readable reason the run failed (e.g. quota exhaustion). Only set when
   * `status === 'FAILED'`; `null`/absent otherwise and cleared on retry. Drives
   * the FailedStateCard message so the operator sees the real cause instead of a
   * generic "sample enrichment failed".
   */
  failureReason?: string | null;
  /** Real-time per-stage breakdown (ENRICHMENT → POST_SCORING); absent on
   *  older responses. Drives the live sub-step list in the loader. */
  liveStage?: LiveStage | null;
}

/**
 * Per-attribute baselines from the prior content-scoring run, copied into the sample run's
 * quality-score response. Fields are dynamic — the backend returns whatever attributes were
 * scored. Every value is `null` when the sample fell back to a random selection.
 */
export type SampleContentAttributeScores = Record<string, number | null>;

export interface SampleCategoryScore {
  category: string;
  score: number;
  productCount: number;
}

/** Per-category attribute breakdown — one entry per category the sample/catalog
 *  spans, each carrying its own `contentAttributeScores`-shaped map so the
 *  category filter can swap in a category-scoped chart instead of the
 *  catalog-wide one. */
export interface SampleCategoryAttributeScore {
  category: string;
  productCount: number;
  attributeScores: SampleContentAttributeScores;
}

/**
 * Aggregate Before/After/Lift quality scores for the latest sample-
 * enrichment run. Drives the comparison card + per-attribute bar chart on
 * the Review & Refine tab.
 */
export interface SampleEnrichmentQualityScoreResponse {
  experimentId: string;
  workflowId: string;
  productCount: number;
  /** Wire value is 0–10 (`avg(original_score)` across the sample); `useSampleEnrichmentQualityScore`'s
   *  `select` scales it ×10 to 0–100 for display. */
  beforeScore: number;
  /** Wire value is 0–10 (`avg(optimized_score)` across the sample); scaled ×10 to 0–100 by the hook. */
  afterScore: number;
  /** Delta: `afterScore - beforeScore`, in the same (post-scaling) 0–100 units. */
  lift: number;
  /**
   * Count of enriched products whose `optimized_score` still fell below the backend's
   * improvement threshold (currently 70/100). Powers the "Needs Improvement" badge on the
   * Review & Refine card. `null`/omitted on older API versions — UI treats that as 0.
   */
  needsImprovement?: number | null;
  /**
   * `needsImprovement / productCount × 100`, rounded to one decimal. `null` when
   * `productCount = 0` (no enriched rows yet — distinguishes "no data" from "0%").
   */
  needsImprovementPct?: number | null;
  /** Per-attribute baselines (0–10) — all `null` for random-sample runs. */
  contentAttributeScores: SampleContentAttributeScores;
  /** Pre-enrichment per-attribute scores (0–10), keyed identically to `contentAttributeScores`.
   *  Optional — absent on runs scored before the backend started returning it; the chart then
   *  falls back to the after-only presentation. Attributes never scored before are omitted. */
  contentAttributeScoresBefore?: SampleContentAttributeScores | null;
  /** Per-category breakdown — not yet consumed by the UI but kept on the type for future. */
  categoryScores: SampleCategoryScore[];
  /** Per-category attribute breakdown — drives the category filter on the attribute
   *  chart. Optional — absent on runs scored before the backend started returning it. */
  categoryAttributeScores?: SampleCategoryAttributeScore[] | null;
  /** Pre-enrichment per-category attribute breakdown, keyed identically to
   *  `categoryAttributeScores`. Optional — a category missing here falls back to
   *  an after-only presentation for that category, matching `contentAttributeScoresBefore`. */
  categoryAttributeScoresBefore?: SampleCategoryAttributeScore[] | null;
  /** Meta Catalog Scorer aggregate data — present only when connectorCode = META. */
  metaDetails?: MetaDetails | null;
}

// ---------------------------------------------------------------------------
// Structured enriched-field value shapes
// ---------------------------------------------------------------------------

/** `structuredTitle` / `structuredDescription` wire shape. */
export interface StructuredContentValue {
  digital_source_type: string;
  /** The actual generated text — the only field rendered in the table. */
  content: string;
}

/**
 * One row inside the `productDetail` array.
 * All fields are optional — the API can return only `attribute_value` (a
 * newline-delimited blob) or the full structured triple.
 */
export interface ProductDetailItem {
  // camelCase variants (backward-compat)
  sectionName?: string;
  attributeName?: string;
  attributeValue?: string;
  // snake_case variants (current API shape)
  section_name?: string;
  attribute_name?: string;
  attribute_value?: string;
}

/** One Q&A pair inside the `productQa` array. */
export interface ProductQaItem {
  q: string;
  a: string;
}

// ---------------------------------------------------------------------------
// Sample enrichment preview
// ---------------------------------------------------------------------------

/**
 * One row in the sample-preview table. Fixed scalar fields (`productId`,
 * `title`, `imageLink`, `score`) are always present. Every enriched field
 * below is nullable — the API omits absent fields via `@JsonInclude(NON_NULL)`.
 * The table derives its visible column set dynamically from whichever fields
 * are non-null across the product set.
 */
export interface SampleEnrichmentPreviewProduct {
  productId: string;
  /** GMC merchant-feed shipping/logistics attributes. Present only with
   *  `?includeSourceShipping=true` AND when the feed states at least one. */
  sourceShippingAttributes?: SourceShippingAttributes;
  /** Platform-native product id (e.g. Google product id). Used to look up pre-enrichment
   *  ("before") catalog data via the products/{connectionId}/list endpoint. */
  externalId?: string;
  title: string;
  imageLink: string;
  /** Lily-processed image variants (e.g. background-removed/cropped). Prefer
   *  `optimizedImages[0]` over `imageLink` when present — `imageLink` is often
   *  empty on enriched rows. */
  optimizedImages?: string[] | null;
  /**
   * Post-enrichment overall score (0–10), from
   * `experiment_content_score.post_overall_score` (workflow-service switched to
   * this source in 2026-05). NULL — and omitted from the JSON via the backend's
   * `@JsonInclude(NON_NULL)` — until the post-enrichment re-scoring stage has run
   * for the product, so consumers MUST guard before calling `.toFixed()`.
   */
  score: number | null;
  /** Pre-enrichment overall score (0–10) for the same product — the baseline `score`
   *  is measured against. Optional: absent on runs from before the backend started
   *  returning it, in which case the UI shows the enriched score with no comparison. */
  originalScore?: number | null;
  // ---- enriched text fields (attribution list) ----
  structuredTitle?: StructuredContentValue | null;
  shortTitle?: string | null;
  description?: string | null;
  structuredDescription?: StructuredContentValue | null;
  /** Category path, e.g. "Clothing > Underwear > Men's Underwear > Boxers". */
  productType?: string | null;
  color?: string | null;
  material?: string | null;
  pattern?: string | null;
  multipack?: string | null;
  isBundle?: boolean | null;
  googleProductCategory?: string | null;
  productLength?: string | null;
  productWidth?: string | null;
  productHeight?: string | null;
  productWeight?: string | null;
  documentLink?: string | null;
  itemGroupTitle?: string | null;
  popularityRank?: number | null;
  // ---- enriched array / structured fields (attribution list) ----
  productHighlight?: string[] | null;
  productDetail?: ProductDetailItem[] | null;
  questionAndAnswer?: ProductQaItem[] | null;
  relatedProduct?: string | string[] | null;
  variantOption?: string | string[] | null;

  // ---- observed on `/sample-enrichment/preview` but absent from the contract
  // doc. Declared from a live payload (2026-08) so the detail modal reads them
  // type-checked rather than by dynamic key. Every one is optional and commonly
  // empty: a sample product is only partly enriched. ----
  /** Backend category label (e.g. "apparel-and-accessories"). */
  category?: string | null;
  /** Q&A pairs. The wire spells this `productQa`; `questionAndAnswer` above is
   *  the attribution-list spelling — readers accept either. */
  productQa?: ProductQaItem[] | null;
  features?: string[] | null;
  specs?: string[] | null;
  shapesAndSilhouettes?: string[] | null;
  themesAndOccasions?: string[] | null;
  styleAndAesthetic?: string[] | null;
  compatibleAccessories?: string[] | null;
  compatibleWith?: string[] | null;
  substituteProducts?: string[] | null;
  bundleSuggestions?: string[] | null;
  sustainabilityAttributes?: string[] | null;
  /** `regular` | `plus` — the fit bucket, not a garment size. */
  sizeType?: string | null;
  materialOrigin?: string | null;
  certification?: string | null;
  careInstruction?: string | null;
  consumerNotice?: string | null;
  /** Suggested shots the merchant could add; advisory, not generated assets. */
  additionalImageRecommendation?: string[] | null;
  lifestyleImageRecommendation?: string[] | null;
  /** Merchant's own identifier, echoed back. Frequently an empty string. */
  merchantItemId?: string | null;
  nativeCommerce?: boolean | null;
  customLabel0?: string | null;
  customLabel1?: string | null;
  customLabel2?: string | null;
  customLabel3?: string | null;
  customLabel4?: string | null;
}

export interface SampleEnrichmentPreviewResponse {
  workflowId: string;
  products: SampleEnrichmentPreviewProduct[];
  page: number;
  size: number;
  totalProducts: number;
  totalPages: number;
}

/**
 * One product row from GET /experiments/{id}/pipeline/enriched-products.
 * Shares enriched-field shapes with `SampleEnrichmentPreviewProduct` but
 * carries `externalId` + `category` alongside `imageLink` (used as the
 * product thumbnail in the preview table).
 */
export interface ExperimentPipelineEnrichedProduct {
  productId: string;
  /** GMC merchant-feed shipping/logistics attributes. Present only with
   *  `?includeSourceShipping=true` AND when the feed states at least one. */
  sourceShippingAttributes?: SourceShippingAttributes;
  /** Platform-native product identifier (e.g. Google product ID). */
  externalId: string;
  /** Which enrichment pass produced this row. `SAMPLE` rows surface when the full
   *  pass had nothing new to curate (every candidate already curated-clean).
   *  Null/absent = pre-migration row → treat as `FULL`. Marks origin, not quality. */
  enrichmentPhase?: 'SAMPLE' | 'FULL' | null;
  title: string;
  /** Product image URL — used as a thumbnail in the preview table. */
  imageLink?: string | null;
  /** Lily-processed image variants (e.g. background-removed/cropped). Prefer
   *  `optimizedImages[0]` over `imageLink` when present — `imageLink` is often
   *  empty on enriched rows. */
  optimizedImages?: string[] | null;
  /** Backend category label (e.g. "home"). */
  category?: string | null;
  // ---- enriched text fields (attribution list) ----
  structuredTitle?: StructuredContentValue | null;
  shortTitle?: string | null;
  description?: string | null;
  structuredDescription?: StructuredContentValue | null;
  /** Category path, e.g. "Clothing > Underwear > Men's Underwear > Boxers". */
  productType?: string | null;
  color?: string | null;
  material?: string | null;
  pattern?: string | null;
  multipack?: string | null;
  isBundle?: boolean | null;
  googleProductCategory?: string | null;
  productLength?: string | null;
  productWidth?: string | null;
  productHeight?: string | null;
  productWeight?: string | null;
  documentLink?: string | null;
  itemGroupTitle?: string | null;
  popularityRank?: number | null;
  // ---- enriched array / structured fields (attribution list) ----
  productHighlight?: string[] | null;
  productDetail?: ProductDetailItem[] | null;
  questionAndAnswer?: ProductQaItem[] | null;
  relatedProduct?: string | string[] | null;
  variantOption?: string | string[] | null;
  score?: number | null;
  /** Pre-enrichment overall score (0–10) — the baseline `score` is measured against.
   *  Optional; absent responses render the enriched score with no comparison. */
  originalScore?: number | null;
}

/** Paginated response from GET /experiments/{id}/pipeline/enriched-products. */
export interface ExperimentPipelineEnrichedProductsResponse {
  workflowId: string;
  products: ExperimentPipelineEnrichedProduct[];
  page: number;
  size: number;
  totalProducts: number;
  totalPages: number;
}

// ---------------------------------------------------------------------------
// Experiment Pipeline — end-to-end downstream run (split → curation →
// enrichment → delivery). The trigger is empty-bodied and the status
// response carries no per-stage counters by design: the operator only
// sees one overall IN_PROGRESS / COMPLETED / FAILED state.
// ---------------------------------------------------------------------------

/**
 * Pipeline lifecycle states. `AWAITING_APPROVAL` parks the pipeline after
 * enrichment finishes; the operator must POST `/pipeline/approval` with a
 * decision before delivery runs. `REJECTED` is the terminal state recorded
 * when the operator declines the gate (the experiment row's `status` flips
 * to `CANCELLED` alongside this). `COMPLETED` / `FAILED` round out the
 * terminal set.
 */
export type ExperimentPipelineStatus =
  | 'IN_PROGRESS'
  | 'AWAITING_APPROVAL'
  | 'COMPLETED'
  | 'REJECTED'
  | 'FAILED'
  | (string & {});

/** Decision recorded for the approval gate before delivery runs. */
export type ExperimentPipelineApprovalDecision = 'APPROVED' | 'REJECTED';

/**
 * Body for `POST /experiments/{id}/pipeline/approval`. The endpoint is only
 * valid while `pipelineStatus === 'AWAITING_APPROVAL'`; a second call after a
 * decision is recorded returns 409 Conflict.
 */
export interface ExperimentPipelineApprovalPayload {
  decision: ExperimentPipelineApprovalDecision;
  /** Optional free-form note attached to the decision (e.g. rejection reason). */
  notes?: string;
  /**
   * Outlier Sensitivity (z-score) the operator confirmed on Review Groups &
   * Outliers, sent with an APPROVED decision so the backend persists the committed
   * value on the experiment row at launch time. The split itself already ran during
   * content scoring; this only records which precomputed sensitivity level is live
   * for analytics. Omitted for REJECTED and for non-split flows (Meta).
   */
  zScoreDefault?: number;
}

/**
 * Optional body for `POST /experiments/{id}/content-scoring/trigger`.
 * All fields optional — omitting the body preserves existing server-default
 * behaviour. `subBatchSize` overrides the `content-scoring.sub-batch-size`
 * env-bound default when present; absent falls back to that default (10).
 */
/**
 * Optional body for `POST /experiments/{id}/content-scoring/trigger`.
 *
 * Carries the post-step flags so the operator's choice reaches the backend at the
 * earliest point in the experiment's life — this is the first request fired after
 * the draft becomes a real experiment.
 *
 * ```jsonc
 * {
 *   "subBatchSize": 10,
 *   "searchDescriptorsEnabled": true,
 *   "phraseDescriptionsEnabled": true
 * }
 * ```
 *
 * TODO(BE): confirm the experiment routes read these. They are optional and
 * backward compatible, so sending them is harmless, but they may be ignored until
 * wired — the same status the scheduled routes had.
 */
export interface ContentScoringTriggerPayload extends EnrichmentPostStepFlags {
  subBatchSize?: number;
}

/**
 * Optional body for `POST /experiments/{id}/pipeline/trigger`
 * (workflow-service `StartExperimentPipelineRequestDto`). All fields optional.
 * NB: since the funnel reorder the FE no longer sends `zScoreDefault` here —
 * generation is triggered from Review & Refine, before the operator picks an
 * outlier sensitivity, so the committed level is sent later with the launch
 * approval (see {@link ExperimentPipelineApprovalPayload}). The field is kept
 * because the backend still accepts it. `startFromStage: 'DELIVERY'` re-attempts
 * only the GMC push on an already-approved run (the Delivery page's retry).
 * `subBatchSize` overrides the env-bound default for the POST re-scoring fan-out
 * batch size; absent falls back to the server default.
 *
 * Carries the post-step flags top-level (no `useCase` object on this route) —
 * this is the run that enriches the experiment's full catalog, so it needs the
 * same configuration the sample ran under:
 *
 * ```jsonc
 * {
 *   "trafficSplit": [50, 50],
 *   "subBatchSize": 10,
 *   "searchDescriptorsEnabled": true,
 *   "phraseDescriptionsEnabled": true
 * }
 * ```
 */
export interface ExperimentPipelineTriggerPayload extends EnrichmentPostStepFlags {
  trafficSplit?: number[];
  zScoreDefault?: number;
  zScoreOverrides?: Record<string, number>;
  startFromStage?: 'CURATION' | 'DELIVERY';
  subBatchSize?: number;
}

export interface ExperimentPipelineTriggerResponse {
  workflowId: string;
  runId: string;
  status: ExperimentPipelineStatus;
}

export interface ExperimentPipelineStatusResponse {
  experimentId: string;
  workflowId: string;
  status: ExperimentPipelineStatus;
  /** ISO timestamp when the pipeline kicked off. */
  startedAt: string;
  /** ISO timestamp once the pipeline reaches a terminal state. */
  completedAt: string | null;
  /** Raw failure detail stamped on a failed run (e.g. the Temporal error string).
   *  Not human-facing — surfaced only behind an "advanced" disclosure. */
  failureReason?: string | null;
  /**
   * Per-stage progress timeline stamped by `ExperimentWorkflowImpl` at each
   * stage boundary (Curation → Enrichment → Approval → Delivery). Same shape
   * as `PipelineStage` in `~/features/experiments/services/experiment-pipeline.service`.
   * `undefined` only on legacy pipeline rows that pre-date the stamping; on
   * any current run the array has at least the in-flight stage. The step-4
   * "Full Catalog Generation" page renders this via `PipelineStageTimeline`
   * instead of the prior 0/1 binary proxy.
   */
  stages?: ExperimentPipelineStage[];
  /** Real-time per-stage breakdown (CURATION → ENRICHMENT → POST_SCORING → …);
   *  absent on older responses. Drives the live sub-step list on the Full
   *  Catalog page (Approval/Delivery are hidden — they are later funnel steps). */
  liveStage?: LiveStage | null;
}

/**
 * Aggregate Before/After/Lift + per-attribute + per-category quality scores
 * from the latest full-catalog pipeline run. Drives the Enrichment Overview
 * page's catalog gauge, comparison card, and per-attribute breakdown.
 * `contentAttributeScores` mirrors the sample run's per-attribute shape so
 * existing UI mappings can be reused.
 */
export interface ExperimentPipelineQualityScoreResponse {
  experimentId: string;
  workflowId: string;
  productCount: number;
  /** Wire value is 0–10 (`avg(original_score)` across the full catalog);
   *  `useExperimentPipelineQualityScore`'s `select` scales it ×10 to 0–100 for display. */
  beforeScore: number;
  /** Wire value is 0–10 (`avg(optimized_score)` across the full catalog); scaled ×10 to 0–100 by the hook. */
  afterScore: number;
  /** Delta: `afterScore - beforeScore`, in the same (post-scaling) 0–100 units. */
  lift: number;
  /** Count of enriched products whose optimized score still fell below the backend's improvement threshold. */
  needsImprovement?: number | null;
  /** `needsImprovement / productCount × 100`, rounded to one decimal. `null` when `productCount = 0`. */
  needsImprovementPct?: number | null;
  /** Per-attribute baselines (0–10). */
  contentAttributeScores: SampleContentAttributeScores;
  /** Pre-enrichment per-attribute scores (0–10), keyed identically to `contentAttributeScores`.
   *  Optional — absent on older runs; the chart then falls back to the after-only presentation. */
  contentAttributeScoresBefore?: SampleContentAttributeScores | null;
  /** Per-category breakdown — reserved for the deferred "Category-Level Quality Score" section. */
  categoryScores: SampleCategoryScore[];
  /** Per-category attribute breakdown — drives the category filter on the attribute
   *  chart. Optional — absent on runs scored before the backend started returning it. */
  categoryAttributeScores?: SampleCategoryAttributeScore[] | null;
  /** Pre-enrichment per-category attribute breakdown, keyed identically to
   *  `categoryAttributeScores`. Optional — a category missing here falls back to
   *  an after-only presentation for that category, matching `contentAttributeScoresBefore`. */
  categoryAttributeScoresBefore?: SampleCategoryAttributeScore[] | null;
  /** Meta Catalog Scorer aggregate data — present only when connectorCode = META. */
  metaDetails?: MetaDetails | null;
}

/** One pipeline stage entry; mirrors `PipelineStageDto` on workflow-service. */
export interface ExperimentPipelineStage {
  name: string;
  /**
   * Wire-permissive: known backend values are `in_progress | completed | awaiting | failed |
   * rejected | skipped`; unknown future values fall through to the "pending" visual in the UX.
   * `rejected` = operator declined the approval gate (BUG-029).
   * `skipped` = stage was intentionally bypassed (e.g. Delivery after a rejection).
   */
  status: string;
  count?: number;
  startedAt?: string;
  completedAt?: string;
  /** When true the stage was skipped — e.g. Delivery suppressed means no products are pushed to the platform. */
  suppressed?: boolean;
}

// ---------------------------------------------------------------------------
// Content Scoring Results — paginated per-product table driven by
// `GET /workflow/v1/experiments/{id}/content-scoring/results`.
//
// Response shape (verified against the workflow-service DTO + fixtures):
//   { totalProducts, successfulProducts, failedProducts, results: [...], page, size, totalPages }
// Each result: { productId, success, result, error }
//   - `result` is a free-form JSONB blob and is `null` for failed products
//     (success=false), which then carry an `error` string.
//   - When present, `result` looks like
//       { product_id?, data_label?, platform_scores: [{ overall_score, field_scores: [{ field_name, score }] }] }
//     `platform_scores` may be empty (e.g. metadata.skipped_llm), and each
//     field score is only guaranteed to carry `field_name` + `score` — the
//     remaining attributes below are agent-dependent and frequently absent.
//   Consumers MUST treat every nested level as optional/nullable.
// ---------------------------------------------------------------------------

/** One attribute's scoring detail within a platform_scores entry. */
export interface ContentScoringFieldScore {
  field_name: string;
  score: number;
  /** Agent-dependent — absent in the current scoring output. */
  weight?: number;
  /** Agent-dependent — absent in the current scoring output. */
  weighted_score?: number;
  /** True when no source data was available for this attribute. Often absent — treat absent as `false`. */
  missing?: boolean;
  /** Human-readable explanation of why this score was assigned. Often absent. */
  feedback?: string;
  /** Original attribute value. Often absent (provisioned for future agent output). */
  provided_value?: string;
}

/** Scores for one ad platform (e.g. "google"). `platform_scores` is an array — one entry per platform. */
export interface ContentScoringPlatformScore {
  platform?: string;
  threshold?: string;
  impact_note?: string;
  field_scores?: ContentScoringFieldScore[];
  overall_score?: number;
}

/** Inner result payload nested inside each `results[]` entry. `null` for failed products. */
export interface ContentScoringResultData {
  product_id?: string;
  data_label?: string;
  platform_scores?: ContentScoringPlatformScore[];
  /** Agent envelope extras (e.g. latency_ms, skipped_llm). */
  metadata?: Record<string, unknown>;
}

/** One row in the content-scoring results list. */
export interface ContentScoringResultItem {
  productId: string;
  /** Product image URL returned directly on each result row. `null`/absent when unavailable. */
  imageUrl?: string | null;
  success: boolean;
  /** `null` for failed products (success=false) — see `error`. */
  result: ContentScoringResultData | null;
  /** Failure reason when `success` is false; `null`/absent otherwise. */
  error?: string | null;
}

export interface ContentScoringResultsResponse {
  totalProducts: number;
  successfulProducts: number;
  failedProducts: number;
  results: ContentScoringResultItem[];
  page: number;
  size: number;
  totalPages: number;
}
